import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../assets/Logo_dash.png';
import {
  LayoutDashboard,
  Users,
  Truck,
  FileBarChart,
  Settings,
} from "lucide-react"

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard/>, path: "/dashboard" },
    { id: "drivers", label: "Manage Drivers", icon: <Users/>, path: "/drivers" },
    { id: "vehicles", label: "Manage Vehicles", icon: <Truck/>, path: "/vehicles" },
    { id: "reports", label: "Report", icon: <FileBarChart/>, path: "/reports" },
    { id: "profile", label: "Settings", icon: <Settings/>, path: "/profile" },
  ];

  const handleMenuClick = (path: string) => {
    navigate(path);
    onClose(); // Close sidebar on mobile after selection
  };

  const isActive = (path: string) => {
    return location.pathname === path || (path === '/dashboard' && location.pathname === '/');
  };

  return (
    <>
      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white text-gray-900 transform transition-transform duration-300 ease-in-out lg:hidden ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-center flex-1">
                <img src={logoImage} alt="JustDeliveries Logo" className="h-10 w-25" />
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 lg:hidden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4">
            <div className="space-y-2">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    isActive(item.path)
                      ? 'bg-jd-green text-white'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:bg-white lg:text-gray-900 lg:h-full lg:flex-col">
        {/* Logo Section */}
        <div className="pb-4 pt-3 border-b border-gray-200">
          <div className="flex items-center justify-center">
            <img src={logoImage} alt="JustDeliveries Logo" className="h-12 w-22" />
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.path)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  isActive(item.path)
                    ? 'bg-jd-green text-white'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
